import packageJson from '../../../../../../../../package.json';
import { IMyWindow } from '@aca-new/app/shared/interfaces/my-window.interface';
import { ECookieKeys } from '@aca-new/app/shared/models/enums/cookie-keys.enum';
import { AppLocationService } from '@aca-new/app/shared/services/browser-services/app-location/app-location-service';
import { CookieStorageService } from '@aca-new/app/shared/services/storage-services/cookie-storage/cookie-storage.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { LOGROCKET_ENVIRONMENT_ID } from '@aca-new/app/shared/tokens/logrocket-enviroment-id.token';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from 'lodash/index';
// eslint-disable-next-line @typescript-eslint/naming-convention
import LogRocket from 'logrocket';

interface IRequest {
  body?: string;
  credentials?: string;
  headers: { [key: string]: string | undefined };
  method: string;
  mode?: string;
  referrer?: string;
  reqId: string;
  url: string;
}

interface IResponse {
  body?: string;
  headers: { [key: string]: string | undefined };
  method: string;
  reqId: string;
  status?: number;
  url: string;
}

const LOGROCKET_SENSITIVE_NETWORK_PATHS: readonly string[] = [
  'api/authenticate', // Login page
  'api/account/set-password', // Forgot password page
  'api/account/change-password', // Change password page
  'api/invitation/inspector/', // Inspectors invitation confirmation page
  'api/factories/invite', // Factories invitation confirmation page
  'api/invitation/auditor/', // Auditors invitation confirmation page
  'payment/adyen',
  '/adyen/',
  'common-payment',
];

@Injectable({
  providedIn: 'root',
})
export class LogrocketInitService {
  private _isInitialized: boolean = false;

  public constructor(
    @Inject(LOGROCKET_ENVIRONMENT_ID) private readonly _logrocketEnvironmentId: Readonly<string>,
    @Inject(WINDOW) private readonly _windowRef: IMyWindow,
    private readonly _appLocationService: AppLocationService,
    private readonly _appUserSettingService: AppUserSettingService,
    private readonly _cookieStorage: CookieStorageService,
    private readonly _appAuthenticationService: AppAuthenticationService,
    private readonly _translateService: TranslateService
  ) {}

  /**
   * @description
   * Initializes LogRocket once based on the current route and session.
   * @returns {void} Nothing.
   */
  public init(): void {
    if (this._isInitialized || !this._logrocketEnvironmentId || isEmpty(this._logrocketEnvironmentId.trim())) {
      return;
    }

    const token = this._cookieStorage.get(ECookieKeys.AUTH_TOKEN);
    const sessionId = this._cookieStorage.get(ECookieKeys.SESSION_ID);
    const isTestCompany = this._appUserSettingService.getIsTestCompany();
    const isAuthenticated = Boolean(token && sessionId);
    const shouldAllowUnauthenticated = this._isPaymentPortalRoute(this._windowRef.location.pathname);

    if (isTestCompany || (!isAuthenticated && !shouldAllowUnauthenticated)) {
      return;
    }

    try {
      LogRocket.init(this._logrocketEnvironmentId, this._getLogRocketInitOptions());
      this._isInitialized = true;
    } catch {
      // LogRocket may be blocked by the browser or unavailable.
    }
  }

  /**
   * @description
   * Identifies the current LogRocket session using authenticated user traits or Anonymous.
   * @param {Record<string, string | boolean>} [extraTraits] - Optional caller-specific traits merged on top.
   * @returns {void} Nothing.
   */
  public identifySession(extraTraits: Record<string, string | boolean> = {}): void {
    let identifyId = '';
    let userTraits = {};
    const userData = this._appAuthenticationService.getCurrentUser();
    const selectedLanguage = this._translateService.currentLang;

    if (userData?.id) {
      identifyId = userData.login ?? '';
      userTraits = {
        name: userData.login,
        id: userData.id,
        email: userData.email,
        company: userData.company.name,
        companyId: userData.company.id,
      };
    } else {
      identifyId = 'Anonymous';
      userTraits = {
        name: 'Anonymous',
        email: '',
        company: '',
        companyId: '',
      };
    }

    try {
      LogRocket.identify(identifyId, {
        ...userTraits,
        selectedLanguage,
        ...extraTraits,
      });
    } catch {
      // LogRocket may be unavailable before init or on blocked sessions.
    }
  }

  /**
   * @description
   * Tracks a LogRocket custom event.
   * @param {string} eventName - Event name.
   * @param {Record<string, string | boolean>} properties - Event properties.
   * @returns {void} Nothing.
   */
  public trackEvent(eventName: string, properties: Record<string, string | boolean>): void {
    try {
      LogRocket.track(eventName, properties);
    } catch {
      // LogRocket may be unavailable before init or on blocked sessions.
    }
  }

  /**
   * @description
   * Identifies the LogRocket session after login and tracks the application version.
   * @returns {void} Nothing.
   */
  public identifyAfterLogin(): void {
    this.identifySession();
    this.trackEvent('application', { version: packageJson.version });
  }

  /**
   * @description
   * Updates the current session language trait in LogRocket.
   * @param {string} language - Active translate locale (same codes as the language selector).
   * @returns {void} Nothing.
   */
  public syncSelectedLanguage(language: string): void {
    this.trackEvent('language_context_update', { selectedLanguage: language });

    const userData = this._appAuthenticationService.getCurrentUser();

    if (!userData.id) {
      return;
    }

    this.identifySession();
  }

  public requestSanitizerLogRocket(request: IRequest): IRequest {
    const requestUrl = request?.url?.toLowerCase() ?? '';

    if (LOGROCKET_SENSITIVE_NETWORK_PATHS.some((path: string): boolean => requestUrl.includes(path))) {
      request.body = undefined;
    }

    request.headers['Authorization'] = undefined;
    request.headers['Cookie'] = undefined;

    return request;
  }

  public responseSanitizerLogRocket(response: IResponse): IResponse {
    const responseUrl = response.url.toLowerCase();

    if (LOGROCKET_SENSITIVE_NETWORK_PATHS.some((path: string): boolean => responseUrl.includes(path))) {
      response.body = undefined;
    }

    response.headers['authorization'] = undefined;

    return response;
  }

  /**
   * @param {string} pathname - Current browser pathname.
   * @returns {boolean} Whether the route belongs to the payment portal.
   */
  private _isPaymentPortalRoute(pathname: string): boolean {
    return pathname === '/pay' || pathname.startsWith('/pay/') || pathname === '/pay-pre-filled' || pathname.startsWith('/pay-pre-filled/');
  }

  /**
   * @returns {object} LogRocket init options.
   */
  private _getLogRocketInitOptions(): object {
    return {
      console: {
        shouldAggregateConsoleErrors: true,
      },
      dom: {
        inputSanitizer: false,
        privateAttributeName: 'data-private',
        textSanitizer: false,
      },
      network: {
        requestSanitizer: this.requestSanitizerLogRocket,
        responseSanitizer: this.responseSanitizerLogRocket,
      },
      mergeIframes: true,
      childDomains: [this._appLocationService.getAcaUrl()],
    };
  }
}
